/* eslint-disable react/jsx-props-no-spreading */
import React, { FunctionComponent, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// OL
import OlView from 'ol/View';
import { EventsKey } from 'ol/events';
import BaseEvent from 'ol/events/Event';

// Custom components

import { Paper, Stack, Tab, Tabs } from '@mui/material';
import TabPanel, { tabProps } from '@/ui/Tabs/TabPanel';
import MapContext, { MapContextType } from '@/context/MapContext/MapContext';

// Types
import { ILayerPaneProps } from '@/@types/components/Map/Controls/Custom/LayerTree';
import LayerTree from '../Map/Controls/Custom/LayerTree/LayerTree';
import LegendPane from './LegendPane';
import BaseLayerTreeControl from '../Map/Controls/Custom/BaseLayerTree/BaseLayerTreeControl';

const tabIdentifier = 'maplayers';

const LayersPane: FunctionComponent<ILayerPaneProps> = (props) => {
  const [eventKey, setEventKey] = useState<EventsKey | null>(null);
  const [tabValue, setTabValue] = React.useState(1);
  const [, setCurrentZoomLevel] = useState<number | undefined>(8);

  const { t } = useTranslation();

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Dont remove this! Without this, Layer Picker doesnt rerender on Map zoom

  const mapContext = useContext(MapContext) as MapContextType;

  const {
    mapId,
    ready,
    layersCollection,
    onLayerVisibilityChange,
    allowSwiperSlider,
    setSwipeValue,
    swipeState,
    setLayerSwipeState,
    baseLayersGroup,
  } = props;

  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const showBaseLayerSwitcher = !!(
    mapContext?.map &&
    baseLayersGroup &&
    baseLayersGroup.getLayers &&
    baseLayersGroup.getLayers().getArray().length > 0
  );

  useEffect(() => {
    if (mapContext.map) {
      const view = mapContext.map.getView();
      const evKey = view.on('change', (evt: BaseEvent) => {
        const v = evt.target as OlView;
        const newZoomLevel = v.getZoom();
        setCurrentZoomLevel(newZoomLevel);
      });

      setEventKey(evKey);
    }

    return () => {
      if (mounted) {
        if (mapContext?.map && eventKey) {
          const view = mapContext.map.getView();
          view.un('change', eventKey.listener);
        }
      }
    };
  }, [mapContext.map]);

  const layersArray = baseLayersGroup ? baseLayersGroup.getLayers().getArray() : [];
  const layers = layersCollection.getArray();

  return (
    <Stack gap={1}>
      <Paper elevation={1} sx={{ position: 'sticky', top: 0, zIndex: 1100 }}>
        <Tabs variant="fullWidth" value={tabValue} onChange={handleChangeTab} aria-label="layer tabs" sx={{ height: 50 }}>
          <Tab iconPosition="start" icon={<i className="fg-layer fg-lg" />} label={t("map:layerspane.podloge")} {...tabProps(tabIdentifier, 0)} sx={{ height: 50, minHeight: 50 }} />
          <Tab iconPosition="start" icon={<i className="fg-layers fg-lg" />} label={t("map:layerspane.slojevi")} {...tabProps(tabIdentifier, 1)} sx={{ height: 50, minHeight: 50 }} />
          <Tab iconPosition="start" icon={<i className="fg-map-legend fg-lg" />} label={t("map:layerspane.legenda")} {...tabProps(tabIdentifier, 2)} sx={{ height: 50, minHeight: 50 }} />
        </Tabs>
      </Paper>
      <TabPanel value={tabValue} index={0} tabIdentifier={tabIdentifier}>
        {ready && showBaseLayerSwitcher ?
          <BaseLayerTreeControl layers={layersArray} />
          : null}
      </TabPanel>
      <TabPanel value={tabValue} index={1} tabIdentifier={tabIdentifier}>
        {ready && layers.length > 0 ?
          <LayerTree
            ready={ready}
            layersCollection={layersCollection}
            onLayerVisibilityChange={onLayerVisibilityChange}
            mapId={mapId}
            allowSwiperSlider={allowSwiperSlider}
            setSwipeValue={setSwipeValue}
            swipeState={swipeState}
            setLayerSwipeState={setLayerSwipeState}
          /> : null}
      </TabPanel>
      <TabPanel value={tabValue} index={2} tabIdentifier={tabIdentifier}>
        <LegendPane padding="0px 16px" layersCollection={layersCollection} />
      </TabPanel>
    </Stack>
  );
};

export default LayersPane;
